import React, { FC, useCallback } from 'react';

import { GeneralThemesSettingsDatasyncService } from '@ps-int/general-themes';
import ThemeCard, { ThemeCardProps } from '@ps-int/general-themes-components/components/ThemeCard';
import { ThemeName } from '@ps-int/general-themes/constants';
import cn from 'classnames';
import { connect, useDispatch } from 'react-redux';

import { RootState } from '../../../../../../init-store';
import { i18n } from '../../../../helpers/i18n';
import { countMetrika } from '../../../../helpers/metrika';
import { setTheme } from '../../../config';
import { getThemeName, isNewYearThemeAvailable } from '../../../config/selectors';
import { isMobileUa } from '../../../uatraits/selectors';
import css from './index.css';

const { themes } = require('@ps-int/general-themes/themes');

type ThemeNames = Array<{
    theme: ThemeName;
    displayName: string;
}>;

const mapStateToProps = (state: RootState) => ({
    currentTheme: getThemeName(state),
    isNewYearThemeAvailable: isNewYearThemeAvailable(state),
    isMobile: isMobileUa(state),
});

const connector = connect(mapStateToProps);

type TelemostThemeCardProps = Pick<ThemeCardProps, 'className' | 'isVideo' | 'thumbnail' | 'background'>;

const getThemeCardProps = (theme: ThemeName): TelemostThemeCardProps => {
    if (theme === ThemeName.GeneralDefault) {
        return {
            className: css.ThemeCard,
            background: `rgb(${themes[ThemeName.GeneralDefault].page.orbOverride['--bg-color']})`,
            isVideo: false,
            thumbnail: '',
        };
    }

    return {
        className: css.ThemeCard,
        isVideo: true,
        thumbnail: themes[theme].thumbnail.webp,
    };
};

const isThemeSelected = (themeName: ThemeName, currentTheme: ThemeName | null) => {
    return themeName === currentTheme || (themeName === ThemeName.GeneralDefault && !currentTheme);
};

type ThemeSelectionGridProps = ReturnType<typeof mapStateToProps> & { ds: GeneralThemesSettingsDatasyncService };

const ThemeSelectionGrid: FC<ThemeSelectionGridProps> = ({ currentTheme, ds, isNewYearThemeAvailable, isMobile }) => {
    const dispatch = useDispatch();

    const themeNames: ThemeNames = [
        {
            theme: ThemeName.GeneralDefault,
            displayName: i18n('settings', 'no-theme'),
        },
        ...(isNewYearThemeAvailable
            ? [
                  {
                      theme: ThemeName.GeneralNewYear2024,
                      displayName: i18n('settings', 'theme-new-year'),
                  },
              ]
            : []),
        {
            theme: ThemeName.GeneralFlow,
            displayName: i18n('settings', 'theme-flow'),
        },
        {
            theme: ThemeName.GeneralDarkflow,
            displayName: i18n('settings', 'theme-dark-flow'),
        },
        {
            theme: ThemeName.GeneralGrass,
            displayName: i18n('settings', 'theme-grass'),
        },
    ];

    const handleSelectTheme = useCallback(
        (theme: ThemeName) => {
            countMetrika('interface elements', 'settings popup', 'change theme', theme || 'default');
            dispatch(setTheme({ theme, settings: ds.getCommonSettings() }));
        },
        [ds, dispatch]
    );

    return (
        <div className={cn(css.ThemeGrid, { [css.ThemeGrid_withNewYear]: isNewYearThemeAvailable })} role="radiogroup">
            {themeNames.map(({ displayName, theme }) => {
                const { className, isVideo, thumbnail, background } = getThemeCardProps(theme);

                return (
                    <ThemeCard
                        ds={ds}
                        className={className}
                        background={background}
                        key={theme}
                        selected={isThemeSelected(theme, currentTheme)}
                        themeName={theme}
                        displayName={displayName}
                        isVideo={!isMobile && isVideo}
                        show360Label={false}
                        thumbnail={thumbnail}
                        onSelectTheme={handleSelectTheme}
                    />
                );
            })}
        </div>
    );
};

export default connector(ThemeSelectionGrid);
